import { OptionsDrawerHeaderProps } from './props';
import styles from './styles.module.scss';
import { Input, ModalHeader } from '@starsoft/common/components';
import { FieldValues } from 'react-hook-form';

/**
 * Header component for the options drawer in an autocomplete menu selector
 *
 * @template T - Type extending FieldValues from react-hook-form
 * @template V - Type of the option value
 *
 * @param {Object} props - Component props
 * @param {string} props.search - Current search input value
 * @param {Function} props.setSearch - Function to update search value
 * @param {Function} props.setOpen - Function to control drawer open state
 * @param {string} props.title - Title text to display in header
 * @param {boolean} props.isContainerColor - Whether to use container background color
 * @param {boolean} props.hideSearch - Whether to hide the search input
 * @param {Object} props.customComponents - Custom component overrides
 * @returns {JSX.Element} Header component with optional search input
 */
export default function OptionsDrawerHeader<T extends FieldValues, V>({
  search,
  setSearch,
  setOpen,
  title,
  isContainerColor,
  hideSearch,
  customComponents,
}: OptionsDrawerHeaderProps<T, V>) {
  /**
   * Handles closing the options drawer
   */
  function onClose() {
    setOpen(false);
  }

  /**
   * Handles search input changes
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event - Change event from input
   */
  function handleSearch(event: React.ChangeEvent<HTMLInputElement>) {
    setSearch(event.target.value);
  }

  return (
    <div className={styles.container}>
      <ModalHeader title={title} onClose={onClose} smallPadding />

      {!hideSearch &&
        (!!customComponents?.customSearchInput ? (
          customComponents?.customSearchInput()
        ) : (
          <div className={styles.container__padded}>
            <Input
              type="text"
              value={search}
              isContainerColor={isContainerColor}
              onChange={handleSearch}
              icon="fad fa-search"
            />
          </div>
        ))}
    </div>
  );
}
